import api from './api'
import {
  AgendamentoPendente,
  LembreteAgendamento,
  Reuniao,
  ReuniaoRequest,
  AtualizarStatusReuniaoRequest,
  StatusReuniao,
} from '@/types/reuniao'
import { PageResponse } from '@/types/comum'

const BASE_URL = '/api/v1/reunioes'

export const reuniaoService = {
  listar: async (params: {
    page?: number
    size?: number
    consultorId?: string
    status?: StatusReuniao
    inicio?: string
    fim?: string
  } = {}): Promise<PageResponse<Reuniao>> => {
    const sp = new URLSearchParams()
    sp.append('page', String(params.page ?? 0))
    sp.append('size', String(params.size ?? 50))
    sp.append('sort', 'dataInicio,desc')
    if (params.consultorId) sp.append('consultorId', params.consultorId)
    if (params.status) sp.append('status', params.status)
    if (params.inicio) sp.append('inicio', params.inicio)
    if (params.fim) sp.append('fim', params.fim)
    const response = await api.get<PageResponse<Reuniao>>(`${BASE_URL}?${sp.toString()}`)
    return response
  },

  obter: async (id: string): Promise<Reuniao> => {
    const response = await api.get<Reuniao>(`${BASE_URL}/${id}`)
    return response
  },

  listarPorOportunidade: async (oportunidadeId: string): Promise<Reuniao[]> => {
    const response = await api.get<Reuniao[]>(`${BASE_URL}/oportunidade/${oportunidadeId}`)
    return response
  },

  /**
   * Resumo (1 reunião relevante por oportunidadeId) em batch.
   * Usado pela tela de Estruturação para mostrar a próxima reunião
   * direto em cada card sem fazer N requests.
   *
   * Retorna um Map<oportunidadeId, Reuniao> — oportunidades sem reunião
   * vinculada simplesmente não aparecem no map.
   */
  resumoPorOportunidades: async (
    oportunidadeIds: string[]
  ): Promise<Record<string, Reuniao>> => {
    if (oportunidadeIds.length === 0) return {}
    const response = await api.post<Record<string, Reuniao>>(
      `${BASE_URL}/resumo-por-oportunidade`,
      oportunidadeIds
    )
    return response as unknown as Record<string, Reuniao>
  },

  criar: async (request: ReuniaoRequest): Promise<Reuniao> => {
    const response = await api.post<Reuniao>(BASE_URL, request)
    return response
  },

  atualizar: async (id: string, request: ReuniaoRequest): Promise<Reuniao> => {
    const response = await api.put<Reuniao>(`${BASE_URL}/${id}`, request)
    return response
  },

  atualizarStatus: async (
    id: string,
    request: AtualizarStatusReuniaoRequest
  ): Promise<Reuniao> => {
    const response = await api.patch<Reuniao>(`${BASE_URL}/${id}/status`, request)
    return response
  },

  deletar: async (id: string): Promise<void> => {
    await api.delete(`${BASE_URL}/${id}`)
  },

  // ─── Etapa 2: acompanhamento do agendamento (OPERADOR+) ───

  /** Reservas cuja primeira reunião ainda não foi marcada. */
  agendamentosPendentes: async (consultorId?: string): Promise<AgendamentoPendente[]> => {
    const sufixo = consultorId ? `?consultorId=${consultorId}` : ''
    return await api.get<AgendamentoPendente[]>(`${BASE_URL}/agendamentos-pendentes${sufixo}`)
  },

  /** Cobra do consultor o agendamento da primeira reunião. */
  enviarLembrete: async (
    oportunidadeId: string,
    mensagem?: string
  ): Promise<LembreteAgendamento> => {
    return await api.post<LembreteAgendamento>(
      `${BASE_URL}/agendamentos-pendentes/${oportunidadeId}/lembrete`,
      { mensagem: mensagem ?? null }
    )
  },

  /** Histórico de cobranças de uma reserva. */
  historicoLembretes: async (oportunidadeId: string): Promise<LembreteAgendamento[]> => {
    return await api.get<LembreteAgendamento[]>(
      `${BASE_URL}/agendamentos-pendentes/${oportunidadeId}/lembretes`
    )
  },
}
